import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, FileText, ArrowRight, Command } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ profiles: [], blog_posts: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setResults({ profiles: [], blog_posts: [] });
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults({ profiles: [], blog_posts: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(query)}&limit=5`);
        if (response.ok) {
          const data = await response.json();
          setResults(data);
          setSelectedIndex(0);
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Get all results as flat array for keyboard navigation
  const getAllResults = useCallback(() => {
    const allResults = [];
    results.profiles.forEach((p, i) => allResults.push({ type: 'profile', data: p, index: i }));
    results.blog_posts.forEach((b, i) => allResults.push({ type: 'blog', data: b, index: i }));
    return allResults;
  }, [results]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      const allResults = getAllResults();
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && allResults.length > 0) {
        e.preventDefault();
        const selected = allResults[selectedIndex];
        if (selected) {
          navigateToResult(selected);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, getAllResults, onClose]);

  const navigateToResult = (result) => {
    if (result.type === 'profile') {
      navigate(`/profiles/${result.data.slug}`);
    } else if (result.type === 'blog') {
      navigate(`/blog/${result.data.slug}`);
    }
    onClose();
  };

  if (!isOpen) return null;

  const allResults = getAllResults();
  const hasResults = results.profiles.length > 0 || results.blog_posts.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="search-backdrop"
      />

      {/* Modal */}
      <div 
        className="fixed top-[15%] left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl mx-4"
        data-testid="global-search-modal"
      >
        <div 
          className="overflow-hidden shadow-2xl"
          style={{ 
            backgroundColor: '#FFFDF8',
            border: '1px solid rgba(200, 164, 72, 0.3)',
          }}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b" style={{ borderColor: 'rgba(13, 13, 13, 0.1)' }}>
            <Search size={20} style={{ color: '#C8A448' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search profiles, articles..."
              className="flex-1 text-base bg-transparent outline-none"
              style={{ fontFamily: "'EB Garamond', serif", color: '#0D0D0D' }}
              data-testid="search-input"
            />
            {loading && (
              <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#C8A448', borderTopColor: 'transparent' }} />
            )}
            <button
              onClick={onClose}
              className="p-1 transition-opacity hover:opacity-70"
              data-testid="close-search-btn"
            >
              <X size={18} style={{ color: 'rgba(13, 13, 13, 0.4)' }} />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto">
            {query.length < 2 ? (
              <div className="p-8 text-center">
                <p 
                  className="text-sm"
                  style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}
                >
                  Type at least 2 characters to search...
                </p>
              </div>
            ) : !hasResults && !loading ? (
              <div className="p-8 text-center">
                <p 
                  className="text-sm"
                  style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13, 13, 13, 0.5)' }}
                >
                  No results found for "{query}"
                </p>
              </div>
            ) : (
              <>
                {/* Profiles */}
                {results.profiles.length > 0 && (
                  <div>
                    <div 
                      className="px-4 py-2 text-xs uppercase tracking-wider"
                      style={{ 
                        fontFamily: "'DM Mono', monospace",
                        color: 'rgba(13, 13, 13, 0.4)',
                        backgroundColor: 'rgba(13, 13, 13, 0.02)',
                      }}
                    >
                      <User size={12} className="inline mr-2" />
                      Profiles
                    </div>
                    {results.profiles.map((profile, idx) => {
                      const globalIdx = idx;
                      return (
                        <button
                          key={profile.slug}
                          onClick={() => navigateToResult({ type: 'profile', data: profile })}
                          className="w-full flex items-center gap-4 px-4 py-3 text-left transition-colors"
                          style={{ 
                            backgroundColor: selectedIndex === globalIdx ? 'rgba(200, 164, 72, 0.1)' : 'transparent',
                          }}
                          data-testid={`search-result-profile-${profile.slug}`}
                        >
                          {profile.image_url ? (
                            <img 
                              src={profile.image_url} 
                              alt={profile.name}
                              className="w-10 h-10 object-cover rounded-full flex-shrink-0"
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: 'rgba(200, 164, 72, 0.1)' }}
                            >
                              <User size={18} style={{ color: '#C8A448' }} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p 
                              className="text-base truncate"
                              style={{ fontFamily: "'EB Garamond', serif", color: '#0D0D0D' }}
                            >
                              {profile.name}
                            </p>
                            <p 
                              className="text-xs truncate"
                              style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.5)' }}
                            >
                              {profile.title} · {profile.company}
                            </p>
                          </div>
                          <ArrowRight size={16} style={{ color: 'rgba(13, 13, 13, 0.3)' }} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Blog Posts */}
                {results.blog_posts.length > 0 && (
                  <div>
                    <div 
                      className="px-4 py-2 text-xs uppercase tracking-wider"
                      style={{ 
                        fontFamily: "'DM Mono', monospace",
                        color: 'rgba(13, 13, 13, 0.4)',
                        backgroundColor: 'rgba(13, 13, 13, 0.02)',
                      }}
                    >
                      <FileText size={12} className="inline mr-2" />
                      Articles
                    </div>
                    {results.blog_posts.map((post, idx) => {
                      const globalIdx = results.profiles.length + idx;
                      return (
                        <button
                          key={post.slug}
                          onClick={() => navigateToResult({ type: 'blog', data: post })}
                          className="w-full flex items-center gap-4 px-4 py-3 text-left transition-colors"
                          style={{ 
                            backgroundColor: selectedIndex === globalIdx ? 'rgba(200, 164, 72, 0.1)' : 'transparent',
                          }}
                          data-testid={`search-result-blog-${post.slug}`}
                        >
                          {post.image ? (
                            <img 
                              src={post.image} 
                              alt={post.title}
                              className="w-10 h-10 object-cover flex-shrink-0"
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: 'rgba(200, 164, 72, 0.1)' }}
                            >
                              <FileText size={18} style={{ color: '#C8A448' }} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p 
                              className="text-base truncate"
                              style={{ fontFamily: "'EB Garamond', serif", color: '#0D0D0D' }}
                            >
                              {post.title}
                            </p>
                            <p 
                              className="text-xs truncate"
                              style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.5)' }}
                            >
                              {post.category}
                            </p>
                          </div>
                          <ArrowRight size={16} style={{ color: 'rgba(13, 13, 13, 0.3)' }} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div 
            className="px-4 py-3 flex items-center justify-between border-t"
            style={{ 
              borderColor: 'rgba(13, 13, 13, 0.1)',
              backgroundColor: 'rgba(13, 13, 13, 0.02)',
            }}
          >
            <div className="flex items-center gap-4 text-xs" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-[#1A1A1A] border border-[rgba(201,168,76,0.15)] rounded text-[10px] text-[rgba(250,248,244,0.4)]">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-[#1A1A1A] border border-[rgba(201,168,76,0.15)] rounded text-[10px] text-[rgba(250,248,244,0.4)]">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-[#1A1A1A] border border-[rgba(201,168,76,0.15)] rounded text-[10px] text-[rgba(250,248,244,0.4)]">esc</kbd>
                Close
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Hook to manage global search state
export const useGlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K (Mac) or Ctrl+K (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev),
  };
};

export default GlobalSearch;
